/* Spin-the-wheel popup (October 2026) — DOM behaviour.
   Spec: docs/superpowers/specs/2026-09-15-spin-wheel-popup-design.md
   Pure decisions live in spin-wheel-core.js (unit-tested).

   Why one file past 300 lines: its single job is the wheel dialog's DOM
   controller. Splitting drawing/form code into more assets would add
   render-path requests on mobile pages that are already too heavy.

   Flow: gate → Spin (prize picked BEFORE animating) → claim form → Shopify
   customer form posts and reloads → reveal the code for the remembered prize.

   Why a pending flag: the footer/newsletter customer forms trigger the same
   form.posted_successfully? render, so "form succeeded" alone does not mean
   "this visitor just claimed a wheel prize".

   Ownership: sets [data-spin-wheel-active] on the root; email-popup.js stays
   hidden while it is present. */
(function () {
  'use strict';

  var core = window.LTSpinWheelCore;
  var KEY_DISMISSED = 'lt-spin-wheel-dismissed';
  var KEY_PENDING = 'lt-spin-wheel-pending';
  var KEY_CLAIMED = 'lt-spin-wheel-claimed';
  var KEY_RESULT = 'lt-spin-wheel-result';
  var SUPPRESSED_PATHS = ['/cart', '/checkout', '/pages/wishlist', '/challenge'];
  var SPIN_FALLBACK_MS = 4800;
  var WHEEL_RADIUS = 96;
  var LABEL_RADIUS = 60;
  var NAVY = '#012047';
  var GOLD = '#f5b82e';
  var COLORS = ['#0fd96d', '#0a2a52', '#11b4f8'];
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var SUBMIT_GUARD_MS = 8000;
  var PENDING_TTL_MS = 15 * 60 * 1000;

  /* ---------- storage (private mode / blocked storage must not throw) ---------- */

  function storageRead(kind, key) {
    try { return window[kind].getItem(key); } catch (e) { return null; /* storage blocked: behave as empty */ }
  }

  function storageWrite(kind, key, value) {
    try { window[kind].setItem(key, value); } catch (e) { /* storage blocked: nothing to persist */ }
  }

  function storageRemove(kind, key) {
    try { window[kind].removeItem(key); } catch (e) { /* storage blocked: nothing to remove */ }
  }

  /* Pure read: the stored claim if well-formed, expired or not. */
  function readStoredPending() {
    var pending;
    try { pending = JSON.parse(storageRead('sessionStorage', KEY_PENDING) || 'null'); }
    catch (e) { return null; /* malformed entry: treat as no claim */ }
    if (!pending || typeof pending.wedgeId !== 'string' || typeof pending.ts !== 'number') return null;
    return pending;
  }

  function isPendingExpired(pending) {
    return Date.now() - pending.ts > PENDING_TTL_MS;
  }

  /* Pure read: a live pending claim, or null (missing, malformed or expired). */
  function readPending() {
    var pending = readStoredPending();
    return pending && !isPendingExpired(pending) ? pending : null;
  }

  function removeExpiredPending() {
    var pending = readStoredPending();
    if (pending && isPendingExpired(pending)) storageRemove('sessionStorage', KEY_PENDING);
  }

  /* The prize a visitor already spun, so reloading can't re-roll it. */
  function readResult() {
    try {
      var saved = JSON.parse(storageRead('localStorage', KEY_RESULT) || 'null');
      return saved && typeof saved.wedgeId === 'string' ? saved : null;
    } catch (e) {
      return null; /* malformed entry: treat as not spun */
    }
  }

  /* ---------- helpers ---------- */

  /* Match against the path without the locale prefix (/ar/cart → /cart). */
  function pathSuppressed() {
    var p = window.location.pathname;
    var localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (localeRoot !== '/' && p.indexOf(localeRoot) === 0) p = '/' + p.slice(localeRoot.length);
    for (var i = 0; i < SUPPRESSED_PATHS.length; i++) {
      if (p === SUPPRESSED_PATHS[i] || p.indexOf(SUPPRESSED_PATHS[i] + '/') === 0) return true;
    }
    return false;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Avoid two same-coloured neighbours where the circle closes. */
  function wedgeColor(i, count) {
    if (count % 3 === 1 && i === count - 1) return COLORS[1];
    return COLORS[i % 3];
  }

  function polar(angleDeg, r) {
    var a = angleDeg * Math.PI / 180;
    return (r * Math.sin(a)).toFixed(3) + ' ' + (-r * Math.cos(a)).toFixed(3);
  }

  function splitLabel(label) {
    if (label.length <= 9 || label.indexOf(' ') === -1) return [label];
    var mid = Math.floor(label.length / 2);
    var left = label.lastIndexOf(' ', mid);
    var right = label.indexOf(' ', mid);
    var at;
    if (left === -1) at = right;
    else if (right === -1) at = left;
    else at = (mid - left <= right - mid) ? left : right;
    return [label.slice(0, at), label.slice(at + 1)];
  }

  /* Gold overrides the normal palette for a highlighted ("jackpot") wedge;
     every other wedge keeps exactly wedgeColor's regular cycle — the
     palette is never recomputed around the gold slice. */
  function wedgeFill(wedge, index, count) {
    return wedge.isHighlighted ? GOLD : wedgeColor(index, count);
  }

  function drawWedgePath(svg, opts) {
    var path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d',
      'M0 0 L' + polar(opts.index * opts.seg, WHEEL_RADIUS) +
      ' A' + WHEEL_RADIUS + ' ' + WHEEL_RADIUS + ' 0 0 1 ' + polar((opts.index + 1) * opts.seg, WHEEL_RADIUS) + ' Z');
    path.setAttribute('fill', wedgeFill(opts.wedge, opts.index, opts.count));
    path.setAttribute('stroke', NAVY);
    path.setAttribute('stroke-width', '1');
    if (opts.wedge.isHighlighted) path.setAttribute('class', 'spin-wheel__wedge--jackpot');
    svg.appendChild(path);
  }

  function drawWedgeLabel(svg, opts) {
    var fill = wedgeFill(opts.wedge, opts.index, opts.count);
    var text = document.createElementNS(SVG_NS, 'text');
    text.setAttribute('class', 'spin-wheel__label-text');
    text.setAttribute('transform', 'rotate(' + (opts.index * opts.seg + opts.seg / 2) + ') translate(0 -' + LABEL_RADIUS + ')');
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('font-size', opts.count > 6 ? '8' : '9.5');
    text.setAttribute('fill', fill === COLORS[1] ? '#ffffff' : NAVY);
    var lines = splitLabel(opts.wedge.label);
    for (var n = 0; n < lines.length; n++) {
      var tspan = document.createElementNS(SVG_NS, 'tspan');
      tspan.setAttribute('x', '0');
      tspan.setAttribute('dy', n === 0 ? (lines.length > 1 ? '-0.2em' : '0.35em') : '1.1em');
      tspan.textContent = lines[n];
      text.appendChild(tspan);
    }
    svg.appendChild(text);
  }

  /* Paths for every non-highlighted wedge first, then the highlighted
     ("jackpot") wedge's path last — SVG paints later elements on top, so
     this keeps its gold fill/glow from being covered by its neighbours.
     Labels are unaffected by paint order and always come after all paths. */
  function drawWheel(svg, wedges) {
    var count = wedges.length;
    var seg = 360 / count;
    var highlighted = [];
    var i;
    for (i = 0; i < count; i++) {
      if (wedges[i].isHighlighted) { highlighted.push(i); continue; }
      drawWedgePath(svg, { wedge: wedges[i], index: i, seg: seg, count: count });
    }
    for (i = 0; i < highlighted.length; i++) {
      drawWedgePath(svg, { wedge: wedges[highlighted[i]], index: highlighted[i], seg: seg, count: count });
    }
    for (i = 0; i < count; i++) {
      drawWedgeLabel(svg, { wedge: wedges[i], index: i, seg: seg, count: count });
    }
  }

  function localizeMonths(select) {
    if (!select || !window.Intl) return;
    try {
      var fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'long', timeZone: 'UTC' });
      for (var i = 0; i < select.options.length; i++) {
        var m = parseInt(select.options[i].value, 10);
        if (m >= 1 && m <= 12) select.options[i].textContent = fmt.format(new Date(Date.UTC(2000, m - 1, 1)));
      }
    } catch (e) { /* no Intl month names: keep numeric months */ }
  }

  function copyText(text, onDone) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(onDone, function () { fallbackCopy(text); onDone(); });
    } else {
      fallbackCopy(text);
      onDone();
    }
  }

  function fallbackCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) { /* copy unsupported: the code stays on screen to copy by hand */ }
  }

  /* ---------- controller ----------
     Every function below takes `wheel`: { root, ui, wedges, state }. */

  function collectUi(root) {
    var q = function (sel) { return root.querySelector(sel); };
    /* Snapshot the Liquid-rendered defaults once, so a no-code wedge's copy
       swap (applyClaimCopy) can always be undone for a coded wedge. */
    var claimPrompt = q('[data-spin-wheel-claim-prompt]');
    var claim = q('[data-spin-wheel-claim]');
    return {
      svg: q('[data-spin-wheel-svg]'),
      spin: q('[data-spin-wheel-spin]'),
      form: q('form'),
      result: q('[data-spin-wheel-result]'),
      claimPrompt: claimPrompt,
      claimPromptDefault: claimPrompt ? claimPrompt.textContent : '',
      tags: q('[data-spin-wheel-tags]'),
      email: q('#spin-wheel-email'),
      day: q('[data-spin-wheel-day]'),
      month: q('[data-spin-wheel-month]'),
      year: q('[data-spin-wheel-year]'),
      clientError: q('[data-spin-wheel-client-error]'),
      successHeadline: q('[data-spin-wheel-success-headline]'),
      codeBox: q('[data-spin-wheel-code-box]'),
      code: q('[data-spin-wheel-code]'),
      note: q('[data-spin-wheel-note]'),
      products: q('[data-spin-wheel-products]'),
      copy: q('[data-spin-wheel-copy]'),
      dismiss: q('[data-spin-wheel-dismiss]'),
      claim: claim,
      claimDefault: claim ? claim.textContent : '',
      serverError: q('[data-spin-wheel-error]'),
      steps: root.querySelectorAll('[data-spin-wheel-step]')
    };
  }

  function setRotation(wheel, rotation, animate) {
    wheel.ui.svg.style.transition = animate ? '' : 'none';
    wheel.ui.svg.style.transform = 'rotate(' + rotation + 'deg)';
  }

  /* Park the wheel, without animating, on a prize chosen earlier. */
  function resumeAtWedge(wheel, index) {
    wheel.state.index = index;
    setRotation(wheel, core.stopAngle(index, wheel.wedges.length, function () { return 0.5; }), false);
  }

  function showStep(wheel, name) {
    var steps = wheel.ui.steps;
    wheel.state.step = name;
    for (var i = 0; i < steps.length; i++) {
      steps[i].hidden = steps[i].getAttribute('data-spin-wheel-step') !== name;
    }
    wheel.ui.dismiss.hidden = name === 'success';
  }

  function focusForStep(wheel) {
    if (wheel.state.step === 'spin') return wheel.ui.spin;
    if (wheel.state.step === 'claim') return wheel.ui.email;
    /* A no-code success hides the code box (Copy is inside it and unfocusable
       while display:none), so focus the headline instead — it carries
       tabindex="-1" precisely so it can receive focus without being tabbable. */
    return wheel.ui.codeBox.hidden ? wheel.ui.successHeadline : wheel.ui.copy;
  }

  function openDialog(wheel) {
    var root = wheel.root;
    if (root.classList.contains('is-open')) return;
    wheel.state.lastFocus = document.activeElement;
    root.hidden = false;
    requestAnimationFrame(function () { root.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
    setTimeout(function () {
      try { focusForStep(wheel).focus(); } catch (e) { /* node detached or unfocusable */ }
    }, 250);
  }

  function closeDialog(wheel) {
    var root = wheel.root;
    var state = wheel.state;
    root.classList.remove('is-open');
    setTimeout(function () { root.hidden = true; }, 240);
    document.body.style.overflow = '';
    if (state.step !== 'success') storageWrite('sessionStorage', KEY_DISMISSED, '1');
    if (state.lastFocus && state.lastFocus.focus) {
      try { state.lastFocus.focus(); } catch (e) { /* previous focus target left the page */ }
    }
  }

  /* A no-code wedge (e.g. Hard luck) swaps in the sign-up-anyway copy; a
     coded wedge restores the Liquid-rendered defaults. Always sets both
     directions so a future re-roll within one page load can't leak one
     wedge's copy into another's claim step. */
  function applyClaimCopy(wheel, hasCode) {
    var ui = wheel.ui;
    var root = wheel.root;
    ui.claimPrompt.textContent = hasCode ? ui.claimPromptDefault : root.getAttribute('data-no-prize-prompt');
    ui.claim.textContent = hasCode ? ui.claimDefault : root.getAttribute('data-no-prize-claim-button');
  }

  function showClaim(wheel) {
    var wedge = wheel.wedges[wheel.state.index];
    showStep(wheel, 'claim');
    applyClaimCopy(wheel, wedge.hasCode);
    /* Write the live region only once it is visible, so it is announced. */
    setTimeout(function () {
      wheel.ui.result.textContent = wedge.hasCode
        ? wheel.root.getAttribute('data-won-template').replace('__PRIZE__', wedge.label)
        : wheel.root.getAttribute('data-no-prize-won');
    }, 50);
    setTimeout(function () {
      try { wheel.ui.email.focus(); } catch (e) { /* node detached or unfocusable */ }
    }, 200);
  }

  function showClientError(wheel, message) {
    if (wheel.ui.serverError) wheel.ui.serverError.hidden = true;
    wheel.ui.clientError.textContent = message;
    wheel.ui.clientError.hidden = false;
  }

  /* The prize's product links are server-rendered inside its span; copy them
     into the visible list. `list` is absent when the section markup is older
     than this script — the two files deploy separately. */
  function showProducts(list, span) {
    if (!list) return;
    var links = span.querySelectorAll('a');
    list.textContent = '';
    for (var i = 0; i < links.length; i++) {
      var item = document.createElement('li');
      item.appendChild(links[i].cloneNode(true));
      list.appendChild(item);
    }
    list.hidden = !links.length;
  }

  function revealCode(wheel, prizeId) {
    var ui = wheel.ui;
    var spans = wheel.root.querySelectorAll('[data-spin-wheel-codes] [data-prize]');
    for (var i = 0; i < spans.length; i++) {
      if (spans[i].getAttribute('data-prize') === prizeId && spans[i].getAttribute('data-code')) {
        ui.code.textContent = spans[i].getAttribute('data-code');
        ui.note.textContent = spans[i].getAttribute('data-note') || '';
        ui.note.hidden = !ui.note.textContent;
        showProducts(ui.products, spans[i]);
        return true;
      }
    }
    return false;
  }

  function spin(wheel) {
    var state = wheel.state;
    if (state.index !== -1) return;
    state.index = core.pickWedge(wheel.wedges, Math.random);
    if (state.index === -1) return;
    var prizeId = wheel.wedges[state.index].id;
    storageWrite('localStorage', KEY_RESULT, JSON.stringify({ wedgeId: prizeId }));

    wheel.ui.spin.disabled = true;
    wheel.ui.spin.textContent = wheel.root.getAttribute('data-spinning-label');
    if (typeof gtag === 'function') {
      gtag('event', 'spin_wheel_spin', { prize_id: prizeId });
    }

    var rotation = core.stopAngle(state.index, wheel.wedges.length, Math.random);
    if (prefersReducedMotion()) {
      setRotation(wheel, rotation, false);
      showClaim(wheel);
      return;
    }
    var hasFinished = false;
    var finish = function () {
      if (hasFinished) return;
      hasFinished = true;
      showClaim(wheel);
    };
    wheel.ui.svg.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, SPIN_FALLBACK_MS);
    requestAnimationFrame(function () { setRotation(wheel, rotation, true); });
  }

  function submitForm(form) {
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }
    var submitter = document.createElement('button');
    submitter.type = 'submit';
    submitter.hidden = true;
    form.appendChild(submitter);
    submitter.click();
    form.removeChild(submitter);
  }

  /* Block double submits while the post (and hCaptcha) is in flight. */
  function holdClaimWhileSubmitting(wheel) {
    wheel.state.isSubmitting = true;
    wheel.ui.claim.disabled = true;
    setTimeout(function () {
      wheel.state.isSubmitting = false;
      wheel.ui.claim.disabled = false;
    }, SUBMIT_GUARD_MS);
  }

  function trackLead(prizeId) {
    if (typeof gtag === 'function') {
      gtag('event', 'generate_lead', { form_type: 'spin_wheel', prize_id: prizeId });
    }
    if (typeof fbq === 'function') {
      fbq('track', 'Lead', { content_category: 'spin_wheel', value: 0, currency: 'EGP' });
    }
  }

  /* The browser never submits on its own: Shopify's hCaptcha listeners
     ignore preventDefault, so validation must happen BEFORE a submit is
     requested. requestSubmit fires a real submit event, so captcha runs. */
  function claim(wheel) {
    var ui = wheel.ui;
    if (wheel.state.index === -1 || wheel.state.isSubmitting) return;
    var today = core.cairoToday(new Date());
    ui.clientError.hidden = true;

    if (!EMAIL_PATTERN.test(ui.email.value.trim())) {
      showClientError(wheel, wheel.root.getAttribute('data-invalid-email'));
      ui.email.focus();
      return;
    }
    if (!core.isValidBirthdate({ d: ui.day.value, m: ui.month.value, y: ui.year.value, today: today })) {
      showClientError(wheel, wheel.root.getAttribute('data-invalid-birthdate'));
      ui.day.focus();
      return;
    }

    holdClaimWhileSubmitting(wheel);
    var prizeId = wheel.wedges[wheel.state.index].id;
    ui.tags.value = core.buildTags({
      d: ui.day.value, m: ui.month.value, y: ui.year.value, wedgeId: prizeId, today: today
    });
    storageWrite('sessionStorage', KEY_PENDING, JSON.stringify({ wedgeId: prizeId, ts: Date.now() }));
    trackLead(prizeId);
    submitForm(ui.form);
  }

  function copyCode(wheel) {
    var ui = wheel.ui;
    var code = (ui.code.textContent || '').trim();
    if (!code) return;
    var original = ui.copy.textContent;
    copyText(code, function () {
      ui.copy.textContent = wheel.root.getAttribute('data-copied-label');
      ui.copy.classList.add('is-copied');
      setTimeout(function () {
        ui.copy.textContent = original;
        ui.copy.classList.remove('is-copied');
      }, 1800);
    });
  }

  function bindEvents(wheel) {
    var root = wheel.root;
    var ui = wheel.ui;
    var close = function () { closeDialog(wheel); };
    var closers = root.querySelectorAll('[data-spin-wheel-close]');
    for (var c = 0; c < closers.length; c++) closers[c].addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && root.classList.contains('is-open')) close();
    });
    ui.spin.addEventListener('click', function () { spin(wheel); });
    ui.claim.addEventListener('click', function () { claim(wheel); });
    /* Enter in the email field would trigger implicit submission. */
    ui.form.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        claim(wheel);
      }
    });
    ui.copy.addEventListener('click', function () { copyCode(wheel); });
  }

  /* Opens the success step for a claimed wedge. A no-code wedge always
     succeeds (there is no code to find); a coded wedge must still resolve
     its code, so a wedge edited/removed after the visitor spun falls
     through to the caller's dismissed branch instead of a fake success. */
  function showSuccessForWedge(wheel, wedge, index) {
    if (wedge.hasCode && !revealCode(wheel, wedge.id)) return false;
    if (!wedge.hasCode) {
      wheel.ui.codeBox.hidden = true;
      wheel.ui.note.hidden = true;
      wheel.ui.successHeadline.textContent = wheel.root.getAttribute('data-no-prize-success');
    }
    wheel.root.setAttribute('data-spin-wheel-active', '1');
    storageWrite('localStorage', KEY_CLAIMED, '1');
    storageRemove('localStorage', KEY_RESULT);
    resumeAtWedge(wheel, index);
    showStep(wheel, 'success');
    openDialog(wheel);
    return true;
  }

  /* Returning from the signup post. Returns true when it opened the dialog. */
  function handlePostResult(wheel) {
    var pending = readPending();
    if (!pending) removeExpiredPending();
    var codesNode = wheel.root.querySelector('[data-spin-wheel-codes]');
    var isPostResult = !!(codesNode || wheel.ui.serverError || /[?&]customer_posted=true/.test(window.location.search));
    if (!pending || !isPostResult) return false;

    storageRemove('sessionStorage', KEY_PENDING);
    /* A non-landable id (unknown, or a 0-odds wedge zeroed after the spin)
       is treated exactly like an unknown id: it falls through below. */
    var pendingIndex = core.indexOfLandableWedge(wheel.wedges, pending.wedgeId);
    var wedge = pendingIndex !== -1 ? wheel.wedges[pendingIndex] : null;

    if (wedge && codesNode && showSuccessForWedge(wheel, wedge, pendingIndex)) {
      return true;
    }

    if (wedge && wheel.ui.serverError) {
      wheel.root.setAttribute('data-spin-wheel-active', '1');
      resumeAtWedge(wheel, pendingIndex);
      showClaim(wheel);
      openDialog(wheel);
      return true;
    }

    /* Posted successfully but the prize can no longer be revealed (e.g. its
       wedge was edited meanwhile): don't offer a second spin this visit. */
    if (codesNode) storageWrite('sessionStorage', KEY_DISMISSED, '1');
    return false;
  }

  /* Already spun on an earlier visit: reopen on the claim step with that
     prize. A saved id that is no longer landable (wedge removed, or zeroed
     out after the visitor spun it) must not be restored either — the
     visitor spins again instead of reclaiming a prize that can't land. */
  function restoreSavedPrize(wheel) {
    var saved = readResult();
    var savedIndex = saved ? core.indexOfLandableWedge(wheel.wedges, saved.wedgeId) : -1;
    if (savedIndex !== -1) {
      resumeAtWedge(wheel, savedIndex);
      showClaim(wheel);
    } else if (saved) {
      storageRemove('localStorage', KEY_RESULT); /* wedge no longer landable */
    }
  }

  /* Normal gating: date window, then visitor/page checks, then a delayed open. */
  function scheduleOpen(wheel) {
    var root = wheel.root;
    var today;
    try {
      today = core.cairoToday(new Date());
    } catch (e) {
      return; /* no Africa/Cairo tz data: fail open to the normal popup */
    }
    if (!core.isWindowActive(root.getAttribute('data-start'), root.getAttribute('data-end'), today)) {
      return;
    }
    root.setAttribute('data-spin-wheel-active', '1');

    if (root.getAttribute('data-logged-in') === '1') return;
    if (pathSuppressed()) return;
    if (storageRead('localStorage', KEY_CLAIMED) === '1') return;
    if (storageRead('sessionStorage', KEY_DISMISSED) === '1') return;

    restoreSavedPrize(wheel);

    var delayMs = parseInt(root.getAttribute('data-delay-ms'), 10);
    setTimeout(function () { openDialog(wheel); }, isNaN(delayMs) ? 3000 : delayMs);
  }

  function init() {
    var root = document.querySelector('[data-spin-wheel]');
    if (!root || !core) return;

    var wedges;
    try {
      wedges = core.drawableWedges(JSON.parse(root.querySelector('[data-spin-wheel-wedges]').textContent));
    } catch (e) {
      return; /* bad owner-edited wedge JSON: no wheel, the normal email popup shows */
    }
    if (!core.isPlayable(wedges)) return;

    var wheel = {
      root: root,
      ui: collectUi(root),
      wedges: wedges,
      state: { step: 'spin', index: -1, isSubmitting: false, lastFocus: null }
    };

    drawWheel(wheel.ui.svg, wedges);
    localizeMonths(wheel.ui.month);
    bindEvents(wheel);

    if (handlePostResult(wheel)) return;

    /* form.errors is shared by every customer form on the page (footer, newsletter). */
    if (wheel.ui.serverError) wheel.ui.serverError.hidden = true;

    scheduleOpen(wheel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
